import SearchBar from "@design-system/Inputs/SearchBar";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import {
    type AiAnswerStatusFilter,
    type QuestionStatusFilter,
    type QuestionType,
} from "@generated/client/graphql";
import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useQueryParam } from "use-query-params";
import { questionnaireParamsArgs } from "../../ctx";
import {
    type ExternalQuestionnaireOptions,
    type QuestionnaireOverviewData,
} from "../../types";
import { EmptyQuestionnairePlaceholder } from "../EmptyQuestionnairePlaceholder/EmptyQuestionnairePlaceholder";
import { FilterChips } from "../FilterChips";
import { QuestionActions } from "../FilterChips/QuestionActions";
import { Questions } from "./components/Questions";
import { Sidebar } from "./components/Sidebar";
import { useQuestionnaireGroups } from "./data";
import { keys, RESOURCE_NAME } from "./i18n";

export const QuestionsTab: FC<{
    questionnaireId?: string;
    options: ExternalQuestionnaireOptions;
    questions: QuestionnaireOverviewData;
    areQuestionsPending: boolean;
}> = ({ questionnaireId, options, questions, areQuestionsPending }) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const [searchString, setSearchString] = useState<string | undefined>(
        undefined,
    );
    const [userSearchString, setUserSearchString] = useState<
        string | undefined
    >(undefined);
    const [ownerIds, setOwnerIds] = useState<string[]>([]);

    const [activeGroupIdParams, setActiveGroupIdParams] = useQueryParam(
        ...questionnaireParamsArgs.group,
    );
    const [questionStatuses, setQuestionStatuses] = useState<
        QuestionStatusFilter[]
    >([]);
    const [aiAnswerStatuses, setAiAnswerStatuses] = useState<
        AiAnswerStatusFilter[]
    >([]);
    const [answerTypes, setAnswerTypes] = useState<QuestionType[]>([]);

    const isEmptyQuestionnaire = !questions?.length;

    const { groups } = useQuestionnaireGroups(questionnaireId);
    useEffect(() => {
        if (groups.length && !activeGroupIdParams) {
            setActiveGroupIdParams(
                groups.filter((group) => group.level === 1)?.[0]?.id,
            );
        }
    }, [groups, activeGroupIdParams, setActiveGroupIdParams]);

    const handleStatusChange = (value: QuestionStatusFilter[]) => {
        setQuestionStatuses(value);
    };

    const handleAiAnswerStatusChange = (value: AiAnswerStatusFilter[]) => {
        setAiAnswerStatuses(value);
    };

    const handleOwnerChange = (value: (string | null)[]) => {
        setOwnerIds(value.filter((id) => id !== null) as string[]);
    };

    const handleAnswerTypeChange = (value: QuestionType[]) => {
        setAnswerTypes(value);
    };

    const shouldShowPlaceholder =
        isEmptyQuestionnaire && !searchString && !areQuestionsPending;

    return (
        <FlexCol gap="4">
            {shouldShowPlaceholder ? (
                <FlexCol gap="3">
                    <FlexRow justifyContent="end">
                        <QuestionActions
                            questionnaireId={questionnaireId}
                            options={options}
                            questions={questions}
                            isQuestionnaireEmpty
                        />
                    </FlexRow>

                    <EmptyQuestionnairePlaceholder
                        isGeneratingQuestions={false}
                    />
                </FlexCol>
            ) : (
                <>
                    <FlexCol gap="3">
                        <FilterChips>
                            <FilterChips.QuestionStatusFilterChip
                                value={questionStatuses}
                                onValueChange={handleStatusChange}
                            />
                            <FilterChips.AnswerTypeFilterChip
                                value={answerTypes}
                                onValueChange={handleAnswerTypeChange}
                            />
                            <FilterChips.AiAnswerStatusFilterChip
                                value={aiAnswerStatuses}
                                onValueChange={handleAiAnswerStatusChange}
                            />
                            <FilterChips.AssigneeFilterChip
                                value={ownerIds}
                                onValueChange={handleOwnerChange}
                                searchFilter={userSearchString}
                                onSearchFilterChange={setUserSearchString}
                            />
                        </FilterChips>

                        <FlexRow justifyContent="between" alignItems="center">
                            <SearchBar
                                placeholder={t(keys.search_bar_placeholder)}
                                searchString={searchString}
                                setSearchString={setSearchString}
                                className="w-[232px]"
                            />

                            <QuestionActions
                                questionnaireId={questionnaireId}
                                options={options}
                                questions={questions}
                                isQuestionnaireEmpty={isEmptyQuestionnaire}
                            />
                        </FlexRow>
                    </FlexCol>

                    <FlexRow gap="4" h="full" w="full">
                        <Sidebar
                            questionnaireId={questionnaireId}
                            activeGroupId={activeGroupIdParams}
                        />
                        <Questions
                            filter={{
                                questionnaireId,
                                searchString,
                                ownerIds,
                                statuses: questionStatuses,
                                aiAnswerStatuses,
                                types: answerTypes,
                            }}
                        />
                    </FlexRow>
                </>
            )}
        </FlexCol>
    );
};
